import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  Popover,
  Typography,
  TextField,
  Chip,
  Tooltip,
  CircularProgress,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Stack,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Business as BusinessIcon,
  LocalShipping as TruckIcon,
  DateRange as DateIcon,
  Warning as WarningIcon,
  CheckCircle,
  Visibility,
  VisibilityOff,
  LocationOn,
  Phone,
  Refresh,
  Search as SearchIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";
import {
  freightList,
  freightComplete,
  freightDelete,
  updateFreight,
  freightPrint,
  partyList,
} from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";
import { pdfGenerator } from "./pdfGenerator";
import { T } from "../../../theme";
import { useDebounce } from "../../../Hooks/useDebounce";
const sq = { borderRadius: "4px !important" };

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryBg,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};
const dangerBtn = {
  ...sq,
  border: `1.5px solid ${T.redBorder}`,
  bgcolor: T.redBg,
  color: T.red,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fee2e2", borderColor: T.red, boxShadow: "none" },
};
const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
};
const successBtn = {
  ...sq,
  border: `1.5px solid ${T.successBorder}`,
  bgcolor: T.successBg,
  color: T.success,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#d1fae5", borderColor: T.success, boxShadow: "none" },
};
const iconBtn = { ...ghostBtn, minWidth: 0, px: 1 };

const inputSx = {
  "& .MuiOutlinedInput-root": {
    ...sq,
    bgcolor: T.white,
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&:hover fieldset": { border: `1.5px solid ${T.borderHover}` },
    "&.Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
    "&.Mui-focused": { boxShadow: `0 0 0 3px ${T.primaryLight}` },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
};

const filterInputSx = {
  "& .MuiInputBase-root": { height: 34, ...sq, bgcolor: T.primaryBg },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
  "& .Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
};

// ── Helpers ────────────────────────────────────────────────────────
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const formatCurrency = (a) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(a || 0);

// ── Dialog Header ──────────────────────────────────────────────────
const DialogHeader = ({ title, onClose }) => (
  <Box
    sx={{
      background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryMid} 100%)`,
      px: 2.5,
      py: 1.75,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <Typography
      sx={{
        fontWeight: 700,
        color: T.white,
        fontSize: "0.88rem",
        letterSpacing: "0.01em",
      }}
    >
      {title}
    </Typography>
    <IconButton
      onClick={onClose}
      size="small"
      sx={{
        color: "rgba(255,255,255,0.8)",
        p: 0.5,
        ...sq,
        "&:hover": { bgcolor: "rgba(255,255,255,0.15)", color: T.white },
      }}
    >
      <CloseIcon sx={{ fontSize: 17 }} />
    </IconButton>
  </Box>
);

// ── ActionButton ───────────────────────────────────────────────────
const ActionButton = ({
  tip,
  icon,
  isDelete,
  isSuccess,
  isEdit,
  isView,
  isPrint,
  onClick,
  disabled,
}) => {
  const c = isDelete
    ? "#dc2626"
    : isEdit
      ? "#757575"
      : isView
        ? "#1976d2"
        : isSuccess || isPrint
          ? T.success
          : T.primary;

  const bg = isDelete
    ? T.redBg
    : isEdit
      ? "#f5f5f5"
      : isView
        ? "#e3f2fd"
        : isSuccess || isPrint
          ? T.successBg
          : T.primaryBg;

  const bd = isDelete
    ? T.redBorder
    : isEdit
      ? "#e0e0e0"
      : isView
        ? "#bbdefb"
        : isSuccess || isPrint
          ? T.successBorder
          : T.border;

  const hBg = isDelete
    ? "#fee2e2"
    : isEdit
      ? "#eeeeee"
      : isView
        ? "#bbdefb"
        : isSuccess || isPrint
          ? "#d1fae5"
          : T.primaryLight;

  return (
    <Tooltip title={tip} arrow placement="top">
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          sx={{
            width: 20,
            height: 20,
            ...sq,
            color: c,
            bgcolor: bg,
            border: `1px solid ${bd}`,
            transition: "all 0.18s",
            "&:hover": {
              bgcolor: hBg,
              borderColor: c,
              transform: "translateY(-1px)",
            },
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 12 } })}
        </IconButton>
      </span>
    </Tooltip>
  );
};

// ── ConfirmPopover ─────────────────────────────────────────────────
const ConfirmPopover = ({
  anchorEl,
  onClose,
  onConfirm,
  title,
  message,
  isSettle,
}) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    transformOrigin={{ vertical: "top", horizontal: "center" }}
    PaperProps={{
      sx: {
        ...sq,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        border: `1.5px solid ${T.border}`,
        bgcolor: T.white,
      },
    }}
  >
    <Box sx={{ p: 2, maxWidth: 240 }}>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            ...sq,
            bgcolor: isSettle ? T.successBg : T.redBg,
            border: `1.5px solid ${isSettle ? T.successBorder : T.redBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isSettle ? (
            <CheckCircle sx={{ fontSize: 20, color: T.success }} />
          ) : (
            <DeleteIcon sx={{ fontSize: 20, color: T.red }} />
          )}
        </Box>
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.78rem",
          color: T.text,
          textAlign: "center",
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.67rem",
          color: T.textSub,
          fontWeight: 500,
          textAlign: "center",
          mb: 1.75,
        }}
      >
        {message}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.75 }}>
        <Button
          fullWidth
          size="small"
          onClick={onClose}
          sx={{ ...ghostBtn, height: 30, fontSize: "0.7rem" }}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          size="small"
          onClick={onConfirm}
          sx={{
            ...(isSettle ? successBtn : dangerBtn),
            height: 30,
            fontSize: "0.7rem",
          }}
        >
          {isSettle ? "Settle" : "Delete"}
        </Button>
      </Box>
    </Box>
  </Popover>
);

// ── MobileFreightCard ──────────────────────────────────────────────
const MobileFreightCard = ({
  row,
  idx,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  onPrint,
  onSettle,
}) => {
  const diffInDays =
    (new Date() - new Date(row.created_at)) / (1000 * 3600 * 24);
  const isOverdue =
    diffInDays > 10 && (!row.paid_amount || Number(row.paid_amount) === 0);
  const accentColor = row.is_completed
    ? T.success
    : isOverdue
      ? T.red
      : row.is_remember
        ? T.warning
        : T.primary;

  const DataPair = ({ label, value, color, xs = 6 }) => (
    <Grid item xs={xs}>
      <Typography
        sx={{
          fontSize: "0.58rem",
          color: T.textMuted,
          fontWeight: 600,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: "0.7rem", fontWeight: 600, color: color || T.text }}
      >
        {value ?? "—"}
      </Typography>
    </Grid>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        ...sq,
        border: `1.5px solid ${expanded ? T.primary : T.border}`,
        bgcolor: T.white,
        overflow: "hidden",
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: expanded ? "0 2px 12px rgba(79,70,229,0.1)" : "none",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
        <Box
          sx={{
            width: 60,
            flexShrink: 0,
            bgcolor: expanded ? T.primary : T.primaryBg,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRight: `1.5px solid ${expanded ? T.primary : T.border}`,
            transition: "background-color 0.2s",
            gap: 0.25,
          }}
        >
          <TruckIcon
            sx={{ fontSize: 20, color: expanded ? T.white : accentColor }}
          />
          <Typography
            sx={{
              fontSize: "0.52rem",
              fontWeight: 700,
              color: expanded ? T.white : T.primary,
            }}
          >
            #{idx + 1}
          </Typography>
          {isOverdue && (
            <Typography
              sx={{
                fontSize: "0.48rem",
                fontWeight: 700,
                color: expanded ? T.white : T.red,
              }}
            >
              {Math.floor(diffInDays)}d
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: 1.5,
            py: 1,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.82rem",
              color: T.text,
              lineHeight: 1.2,
              mb: 0.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.truck_no || "—"}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.65rem",
              color: T.textSub,
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              mb: 0.25,
            }}
          >
            {row.party_name || "—"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <LocationOn sx={{ fontSize: 10, color: T.textMuted }} />
            <Typography
              sx={{
                fontSize: "0.62rem",
                color: T.textMuted,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.from_location || "—"} → {row.to_location || "—"}
            </Typography>
          </Box>
          <Chip
            label={
              row.is_completed ? "Completed" : isOverdue ? "Overdue" : "Pending"
            }
            size="small"
            sx={{
              mt: 0.4,
              width: "fit-content",
              ...sq,
              fontSize: "0.55rem",
              height: 16,
              bgcolor: `${accentColor}14`,
              color: accentColor,
              border: `1px solid ${accentColor}30`,
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-end",
            px: 1.2,
            py: 1,
            minWidth: 100,
          }}
        >
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{ fontSize: "0.55rem", color: T.textMuted, fontWeight: 600 }}
            >
              Net Payable
            </Typography>
            <Typography
              sx={{ fontWeight: 800, fontSize: "0.8rem", color: T.primary }}
            >
              ₹{row.net_payable || 0}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={onToggle}
            startIcon={
              expanded ? (
                <VisibilityOff sx={{ fontSize: 12 }} />
              ) : (
                <Visibility sx={{ fontSize: 12 }} />
              )
            }
            sx={{
              ...ghostBtn,
              height: 26,
              fontSize: "0.62rem",
              px: 1,
              "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
            }}
          >
            {expanded ? "Hide" : "Details"}
          </Button>
        </Box>
      </Box>

      {expanded && (
        <Box
          sx={{
            bgcolor: T.primaryBg,
            borderTop: `1.5px solid ${T.border}`,
            p: 1.5,
          }}
        >
          <Stack spacing={1.25}>
            <Box>
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: T.primary,
                  mb: 0.5,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                📋 Basic Info
              </Typography>
              <Grid container spacing={0.5}>
                <DataPair label="ID" value={row.freight_id} />
                <DataPair
                  label="Loading"
                  value={formatDate(row.loading_date)}
                />
                <DataPair
                  label="Unloading"
                  value={formatDate(row.unloading_date)}
                  xs={12}
                />
              </Grid>
            </Box>

            <Box
              sx={{
                p: 1,
                bgcolor: T.white,
                border: `1px solid ${T.border}`,
                ...sq,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: T.primary,
                  mb: 0.5,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                ⚖️ Weight
              </Typography>
              <Grid container spacing={0.5}>
                <DataPair label="Load" value={row.loading_wt || 0} xs={3} />
                <DataPair label="Recv" value={row.received_wt || 0} xs={3} />
                <DataPair
                  label="Short"
                  value={row.shortage_wt || 0}
                  color={T.red}
                  xs={3}
                />
                <DataPair label="Bill" value={row.billing_wt || 0} xs={3} />
              </Grid>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: T.primary,
                  mb: 0.5,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                💰 Charges
              </Typography>
              <Grid container spacing={0.5}>
                {[
                  ["Rate", `₹${row.freight_rate || 0}`],
                  ["LR Amt", `₹${row.freight_amt || 0}`],
                  ["TDS", `₹${row.tds_amt || 0}`],
                  ["Shortage", `₹${row.shortage_amt || 0}`],
                  ["Commission", `₹${row.commission || 0}`],
                  ["Other", `₹${row.other_exp || 0}`],
                ].map(([l, v]) => (
                  <DataPair key={l} label={l} value={v} />
                ))}
              </Grid>
            </Box>

            <Box
              sx={{
                p: 1,
                bgcolor: T.warningBg,
                border: `1px solid ${T.warningBorder}`,
                ...sq,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: T.warning,
                  mb: 0.5,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                💵 Advance
              </Typography>
              <Grid container spacing={0.5}>
                <DataPair
                  label="Mines"
                  value={`₹${row.ad_mines_exp || 0}`}
                  xs={4}
                />
                <DataPair
                  label="Diesel"
                  value={`₹${row.ad_diesel_exp || 0}`}
                  xs={4}
                />
                <DataPair
                  label="Cash"
                  value={`₹${row.ad_cash_exp || 0}`}
                  xs={4}
                />
                <DataPair
                  label="Total Advance"
                  value={`₹${row.total_advance || 0}`}
                  color={T.warning}
                  xs={12}
                />
              </Grid>
            </Box>

            {(row.truck_owner || row.phone_no || row.pan_no) && (
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    color: T.primary,
                    mb: 0.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  👤 Owner
                </Typography>
                <Grid container spacing={0.5}>
                  {row.truck_owner && (
                    <DataPair label="Owner" value={row.truck_owner} xs={12} />
                  )}
                  {row.phone_no && (
                    <DataPair label="Phone" value={row.phone_no} />
                  )}
                  {row.pan_no && <DataPair label="PAN" value={row.pan_no} />}
                </Grid>
              </Box>
            )}

            <Box
              sx={{
                p: 1,
                bgcolor: T.primaryLight,
                border: `1.5px solid ${T.border}`,
                ...sq,
              }}
            >
              <Grid container spacing={0.5}>
                <DataPair
                  label="Net Amount"
                  value={`₹${row.net_amount || 0}`}
                />
                <DataPair
                  label="Net Payable"
                  value={`₹${row.net_payable || 0}`}
                  color={T.primary}
                />
                <DataPair
                  label="Paid"
                  value={`₹${row.paid_amount?.toFixed(1) || 0}`}
                  color={T.success}
                />
                <DataPair
                  label="Balance"
                  value={`₹${row.bal_amount?.toFixed(1) || 0}`}
                  color={T.red}
                />
              </Grid>
            </Box>

            {row.remark && (
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.62rem",
                    color: T.textMuted,
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  Remark
                </Typography>
                <Typography
                  sx={{ fontSize: "0.72rem", color: T.text, fontWeight: 500 }}
                >
                  {row.remark}
                </Typography>
              </Box>
            )}
          </Stack>

          <Box
            sx={{
              display: "flex",
              gap: 0.75,
              mt: 1.5,
              pt: 1.25,
              borderTop: `1px dashed ${T.border}`,
              justifyContent: "flex-end",
              flexWrap: "wrap",
            }}
          >
            {!row.is_completed && (
              <Button
                size="small"
                startIcon={<CheckCircle sx={{ fontSize: 13 }} />}
                onClick={(e) => onSettle(e, row.freight_id)}
                sx={{ ...successBtn, height: 30, px: 1.5 }}
              >
                Settle
              </Button>
            )}
            <Button
              size="small"
              startIcon={<PrintIcon sx={{ fontSize: 13 }} />}
              onClick={() => onPrint(row)}
              sx={{ ...ghostBtn, height: 30, px: 1.5 }}
            >
              Print
            </Button>
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 13 }} />}
              onClick={() => onEdit(row)}
              sx={{ ...ghostBtn, height: 30, px: 1.5 }}
            >
              Edit
            </Button>
            <Button
              size="small"
              startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}
              onClick={(e) => onDelete(e, row.freight_id)}
              sx={{ ...dangerBtn, height: 30, px: 1.5 }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

// ── Main Component ─────────────────────────────────────────────────
export default function FreightList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [data, setData] = useState([]);
  const [partyFilter, setPartyFilter] = useState("");
  const [tpFilter, setTpFilter] = useState(""); // kept for API
  const [loadingDateFilter, setLoadingDateFilter] = useState(null);
  const [fromFilter, setFromFilter] = useState(""); // kept for API
  const [lrNumberFilter, setLrNumberFilter] = useState("");
  const [truckFilter, setTruckFilter] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState({});
  const [settleAnchorEl, setSettleAnchorEl] = useState(null);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [settleId, setSettleId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [partyOptions, setParties] = useState([]);
  const [excelModalOpen, setExcelModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [expandedCardId, setExpandedCardId] = useState(null);
  const debouncedParty = useDebounce(partyFilter, 500);
  const debouncedTruck = useDebounce(truckFilter, 500);
  const debouncedLrNumber = useDebounce(lrNumberFilter, 500);
  const debouncedLoadingDate = useDebounce(
    loadingDateFilter ? loadingDateFilter.toISOString().split("T")[0] : "",
    500,
  );

  const handleCardToggle = (id) =>
    setExpandedCardId((p) => (p === id ? null : id));

  const fetchMoreParties = useCallback(
    async (reset = false, filters = {}) => {
      setLoading(true);
      try {
        const dataRes = await freightList(reset ? 1 : page, filters);
        if (dataRes?.results?.length) {
          setData((prev) =>
            reset ? dataRes.results : [...prev, ...dataRes.results],
          );
          setPage((prev) => (reset ? 2 : prev + 1));
          setHasMore(!!dataRes.next);
        } else {
          if (reset) setData([]);
          setHasMore(false);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [page],
  );

  useEffect(() => {
    setData([]);
    setPage(1);
    setExpandedCardId(null);
    fetchMoreParties(true, {
      party_name: debouncedParty,
      truck_no: debouncedTruck,
      lr_number: debouncedLrNumber,
      loading_date: debouncedLoadingDate,
      do_no: tpFilter,
      from_to: fromFilter,
    });
  }, [debouncedParty, debouncedTruck, debouncedLrNumber, debouncedLoadingDate]);

  useEffect(() => {
    const fetchParties = async () => {
      try {
        const res = await partyList({ party_type: "consignor" });
        setParties(Array.isArray(res) ? res : []);
      } catch (error) {
        console.error("Failed to fetch parties:", error);
        setParties([]);
      }
    };
    fetchParties();
  }, []);

  const handleComplete = async (id) => {
    await freightComplete(id);
    setData((p) => p.filter((r) => r.freight_id !== id));
    setSnackbar({
      open: true,
      message: "LR completed successfully",
      severity: "success",
    });
    setSettleAnchorEl(null);
    setSettleId(null);
  };

  const handleDelete = async (id) => {
    await freightDelete(id);
    setData((p) => p.filter((r) => r.freight_id !== id));
    setSnackbar({
      open: true,
      message: "Record deleted successfully",
      severity: "success",
    });
    setDeleteAnchorEl(null);
    setDeleteId(null);
  };

  const handleEditOpen = (row) => {
    setEditRow({ ...row });
    setEditDialogOpen(true);
  };

  const handleEditChange = (field, value) => {
    const u = { ...editRow, [field]: value };
    const g = (f, v) => parseFloat(f === field ? v : u[f]) || 0;
    const loadW = g("loading_wt", value),
      recvW = g("received_wt", value);
    const fRate = g("freight_rate", value),
      tdsR = g("tds_per", value),
      sRate = g("shortage_rate", value);
    const comm = g("commission", value),
      mines = g("ad_mines_exp", value);
    const diesel = g("ad_diesel_exp", value),
      cash = g("ad_cash_exp", value),
      other = g("other_exp", value);
    u.billing_wt = Math.min(loadW, recvW).toFixed(2);
    const shortage = (loadW - recvW) * 1000;
    u.shortage_wt = shortage > 0 ? shortage.toFixed(2) : "0";
    u.freight_amt = (fRate * parseFloat(u.billing_wt)).toFixed(2);
    u.tds_amt = ((parseFloat(u.freight_amt) * tdsR) / 100).toFixed(2);
    u.shortage_amt = (parseFloat(u.shortage_wt) * sRate).toFixed(2);
    u.total_advance = (mines + diesel + cash).toFixed(2);
    u.net_amount = (
      parseFloat(u.freight_amt) -
      parseFloat(u.tds_amt) -
      parseFloat(u.shortage_amt) -
      parseFloat(u.total_advance) -
      comm
    ).toFixed(2);
    u.net_payable = (parseFloat(u.net_amount) - other).toFixed(2);
    setEditRow(u);
  };

  const handleEditSave = async () => {
    const s = { ...editRow, receive_amount: editRow.receive_amount || 0 };
    s.bal_amount = (
      parseFloat(s.net_payable || 0) - parseFloat(s.paid_amount || 0)
    ).toFixed(2);
    const res = await updateFreight(s.freight_id, s);
    setData((p) => p.map((r) => (r.freight_id === res.freight_id ? res : r)));
    setEditDialogOpen(false);
    setSnackbar({
      open: true,
      message: "Record updated successfully",
      severity: "success",
    });
  };

  const handlePrintSingleRow = (row) => pdfGenerator(row);

  const handleExportSelectedToExcel = async () => {
    const XLSX = await import("xlsx-js-style");

    const sel = data.filter((r) => selectedRows.includes(r.freight_id));

    const headers = [
      "Sr No",
      "Party Name",
      "LR No",
      "Loading Date",
      "Unloading Date",
      "Truck No.",
      "From",
      "To",
      "Loading Wt",
      "Receiving Wt",
      "Shortage Wt",
      "Billing Wt",
      "LR Rate",
      "LR Amt",
      "TDS %",
      "TDS Amt",
      "Shortage Rate",
      "Shortage Amt",
      "Ad Mines",
      "Ad Diesel",
      "Ad Cash",
      "Total Ad",
      "Commission",
      "Net Amt",
      "Others Exp.",
      "Net Payable",
      "Paid Amt",
      "Bal Amt",
      "Truck Owner",
      "Phone No.",
      "PAN No.",
      "Remark",
      "Date",
    ];

    const rows = sel.map((row, i) => [
      i + 1,
      row.party_name || "—",
      row.lr_number || "—",
      formatDate(row.loading_date),
      formatDate(row.unloading_date),
      row.truck_no || "—",
      row.from_location || "—",
      row.to_location || "—",
      row.loading_wt ?? "—",
      row.received_wt ?? "—",
      row.shortage_wt ?? "—",
      row.billing_wt ?? "—",
      row.freight_rate ?? "—",
      row.freight_amt ?? "—",
      row.tds_per ?? "—",
      row.tds_amt ?? "—",
      row.shortage_rate ?? "—",
      row.shortage_amt ?? "—",
      row.ad_mines_exp ?? "—",
      row.ad_diesel_exp ?? "—",
      row.ad_cash_exp ?? "—",
      row.total_advance ?? "—",
      row.commission ?? "—",
      row.net_amount ?? "—",
      row.other_exp ?? "—",
      row.net_payable ?? "—",
      row.paid_amount ?? "—",
      row.bal_amount ?? "—",
      row.truck_owner || "—",
      row.phone_no || "—",
      row.pan_no || "—",
      row.remark || "—",
      formatDate(row.created_at),
    ]);

    // ── Style Definitions ──────────────────────────────────────────
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10, name: "Segoe UI" },
      fill: { fgColor: { rgb: "4F46E5" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: false },
      border: {
        top: { style: "thin", color: { rgb: "3730A3" } },
        bottom: { style: "thin", color: { rgb: "3730A3" } },
        left: { style: "thin", color: { rgb: "3730A3" } },
        right: { style: "thin", color: { rgb: "3730A3" } },
      },
    };

    const cellStyleEven = {
      font: { sz: 10, name: "Segoe UI", color: { rgb: "1E1B4B" } },
      fill: { fgColor: { rgb: "F5F3FF" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: false },
      border: {
        top: { style: "thin", color: { rgb: "E0E7FF" } },
        bottom: { style: "thin", color: { rgb: "E0E7FF" } },
        left: { style: "thin", color: { rgb: "E0E7FF" } },
        right: { style: "thin", color: { rgb: "E0E7FF" } },
      },
    };

    const cellStyleOdd = {
      ...cellStyleEven,
      fill: { fgColor: { rgb: "FFFFFF" } },
    };

    const numberStyle = {
      ...cellStyleEven,
      font: { ...cellStyleEven.font, bold: true, color: { rgb: "4F46E5" } },
    };

    const numberStyleOdd = {
      ...numberStyle,
      fill: { fgColor: { rgb: "FFFFFF" } },
    };

    // ── Build Worksheet Data ───────────────────────────────────────
    const wsData = [
      headers.map((h) => ({ v: h, t: "s", s: headerStyle })),
      ...rows.map((row, rowIdx) =>
        row.map((val, colIdx) => {
          const isEven = rowIdx % 2 === 0;
          const isNumber = colIdx === 0;

          let style;
          if (isNumber) {
            style = isEven ? numberStyle : numberStyleOdd;
          } else {
            style = isEven ? cellStyleEven : cellStyleOdd;
          }

          return {
            v: val ?? "—",
            t: typeof val === "number" ? "n" : "s",
            s: style,
          };
        }),
      ),
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // ── Column Widths (no wrapping, width based on longest content) ──
    const colWidths = headers.map((_, colIdx) => {
      const allValues = [
        headers[colIdx],
        ...rows.map((r) => String(r[colIdx] ?? "")),
      ];
      const maxLen = Math.max(...allValues.map((v) => v.length));
      return { wch: maxLen + 4 };
    });

    ws["!cols"] = colWidths;

    // ── Row Heights ────────────────────────────────────────────────
    ws["!rows"] = [{ hpt: 18 }];

    // ── Freeze Header Row ──────────────────────────────────────────
    ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2" };

    const wb = XLSX.utils.book_new();
    wb.Props = {
      Title: "LR Report",
      Author: "Staff Management System",
      CreatedDate: new Date(),
    };

    XLSX.utils.book_append_sheet(wb, ws, "LR Report");
    XLSX.writeFile(
      wb,
      `Freight_Report_${new Date().toISOString().split("T")[0]}.xlsx`,
    );

    setExcelModalOpen(false);
    setSelectedRows([]);
  };

  const handleFilter = () => {
    if (!fromDate || !toDate) {
      setSnackbar({
        open: true,
        message: "Please select both dates",
        severity: "error",
      });
      return;
    }
    const s = fromDate.toISOString().split("T")[0];
    const e = toDate.toISOString().split("T")[0];
    setFilteredData(
      data.filter((i) => i.loading_date >= s && i.loading_date <= e),
    );
    setExpandedCardId(null);
  };

  const currentData = filteredData.length > 0 ? filteredData : data;

  const clearFilters = () => {
    setPartyFilter("");
    setTpFilter("");
    setLrNumberFilter(""); // ← updated
    setTruckFilter("");
    setLoadingDateFilter(null);
    setFromFilter("");
    setFromDate(null);
    setToDate(null);
    setFilteredData([]);
  };

  const hasActiveFilters = Boolean(
    partyFilter?.trim() ||
    truckFilter?.trim() ||
    lrNumberFilter?.trim() ||
    loadingDateFilter ||
    fromDate ||
    toDate ||
    filteredData.length > 0,
  );

  const partyNameOptions = partyOptions.map((o) => o.name);
  const truckOptions = [
    ...new Set(data.map((i) => i.truck_no).filter(Boolean)),
  ];

  return (
    <Box
      sx={{ px: 0, pt: 0, mt: -3, maxWidth: 1400, mx: "auto", width: "100%" }}
    >
      {/* ── Toolbar ── */}
      <Box sx={{ mt: 2, mb: 1.5 }}>
        <Grid container spacing={0.75} alignItems="center">
          {/* LR No */}
          <Grid item xs={12} sm={4} md={2.2}>
            <TextField
              size="small"
              placeholder="LR No."
              fullWidth
              value={lrNumberFilter}
              onChange={(e) => setLrNumberFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 14, color: T.primary }} />
                  </InputAdornment>
                ),
                endAdornment: lrNumberFilter && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setLrNumberFilter("")}
                      sx={{ p: 0.25 }}
                    >
                      <CloseIcon sx={{ fontSize: 12, color: T.textMuted }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={filterInputSx}
            />
          </Grid>
          {/* Party */}
          <Grid item xs={12} sm={4} md={2.5}>
            <Autocomplete
              freeSolo
              options={partyNameOptions}
              value={partyFilter}
              onInputChange={(_, v) => setPartyFilter(v || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Party Name"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon sx={{ fontSize: 14, color: T.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={filterInputSx}
                />
              )}
            />
          </Grid>

          {/* Truck */}
          <Grid item xs={12} sm={4} md={2.2}>
            <Autocomplete
              freeSolo
              options={truckOptions}
              value={truckFilter}
              onInputChange={(_, v) => setTruckFilter(v || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Truck No."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <TruckIcon sx={{ fontSize: 14, color: T.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={filterInputSx}
                />
              )}
            />
          </Grid>

          {/* From Date */}
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              type="date"
              size="small"
              label="From Date"
              fullWidth
              placeholder="From Date"
              value={fromDate ? fromDate.toISOString().split("T")[0] : ""}
              onChange={(e) =>
                setFromDate(e.target.value ? new Date(e.target.value) : null)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateIcon sx={{ fontSize: 13, color: T.textMuted }} />
                  </InputAdornment>
                ),
              }}
              sx={filterInputSx}
            />
          </Grid>

          {/* To Date */}
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              type="date"
              size="small"
              label="To Date"
              fullWidth
              value={toDate ? toDate.toISOString().split("T")[0] : ""}
              onChange={(e) =>
                setToDate(e.target.value ? new Date(e.target.value) : null)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateIcon sx={{ fontSize: 13, color: T.textMuted }} />
                  </InputAdornment>
                ),
              }}
              sx={filterInputSx}
            />
          </Grid>

          {/* Search (Date Range) */}
          <Grid item xs="auto">
            <Tooltip title="Search by Date Range" arrow>
              <Button
                size="small"
                onClick={handleFilter}
                sx={{ ...primaryBtn, height: 34, minWidth: 34, px: 1 }}
              >
                <SearchIcon sx={{ fontSize: 15 }} />
              </Button>
            </Tooltip>
          </Grid>
          {/* Spacer */}
          <Grid
            item
            xs={false}
            md
            sx={{ display: { xs: "none", md: "block" } }}
          />

          {/* Clear */}
          <Grid item xs="auto">
            <Tooltip title="Clear All Filters" arrow>
              <span>
                <Button
                  size="small"
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                  sx={{
                    ...dangerBtn,
                    height: 34,
                    minWidth: 34,
                    px: 1,
                    "&.Mui-disabled": {
                      borderColor: T.border,
                      bgcolor: T.white,
                      color: T.textMuted,
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </Button>
              </span>
            </Tooltip>
          </Grid>

          {/* Refresh */}
          <Grid item xs="auto">
            <Tooltip title="Refresh" arrow>
              <Button
                size="small"
                onClick={() =>
                  fetchMoreParties(true, {
                    party_name: debouncedParty,
                    truck_no: debouncedTruck,
                    lr_number: debouncedLrNumber,
                    loading_date: debouncedLoadingDate,
                  })
                }
                sx={{ ...iconBtn, height: 34, minWidth: 34, px: 1 }}
              >
                <Refresh sx={{ fontSize: 15 }} />
              </Button>
            </Tooltip>
          </Grid>

          {/* Export */}
          <Grid item xs="auto">
            <Tooltip title="Export to Excel" arrow>
              <Button
                size="small"
                onClick={() => setExcelModalOpen(true)}
                sx={{ ...iconBtn, height: 34, minWidth: 34, px: 1 }}
              >
                <DownloadIcon sx={{ fontSize: 15 }} />
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* ── Table / Mobile ── */}
      {isMobile ? (
        loading && data.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 10,
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: T.primary }} size={32} />
            <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>
              Loading…
            </Typography>
          </Box>
        ) : currentData.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              border: `1.5px dashed ${T.border}`,
              bgcolor: T.primaryBg,
              ...sq,
            }}
          >
            <TruckIcon sx={{ fontSize: 44, color: T.border, mb: 1 }} />
            <Typography
              sx={{
                fontWeight: 700,
                color: T.text,
                mb: 0.5,
                fontSize: "0.9rem",
              }}
            >
              No freight records found
            </Typography>
          </Box>
        ) : (
          <Box
            id="freight-list-mobile"
            sx={{ maxHeight: "calc(100vh - 220px)", overflow: "auto" }}
          >
            <InfiniteScroll
              dataLength={data.length}
              next={() => fetchMoreParties(false)}
              hasMore={hasMore}
              loader={
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <CircularProgress size={20} sx={{ color: T.primary }} />
                </Box>
              }
              scrollableTarget="freight-list-mobile"
            >
              {currentData.map((row, idx) => (
                <MobileFreightCard
                  key={row.freight_id}
                  row={row}
                  idx={idx}
                  expanded={expandedCardId === row.freight_id}
                  onToggle={() => handleCardToggle(row.freight_id)}
                  onEdit={handleEditOpen}
                  onDelete={(e, id) => {
                    setDeleteAnchorEl(e.currentTarget);
                    setDeleteId(id);
                  }}
                  onPrint={handlePrintSingleRow}
                  onSettle={(e, id) => {
                    setSettleAnchorEl(e.currentTarget);
                    setSettleId(id);
                  }}
                />
              ))}
            </InfiniteScroll>
          </Box>
        )
      ) : (
        <Paper
          elevation={0}
          sx={{
            ...sq,
            border: `1.5px solid ${T.border}`,
            overflow: "hidden",
            boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
            height: "calc(100vh - 160px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TableContainer
            id="freight-list-container"
            sx={{ flex: 1, minHeight: 0, overflow: "auto" }}
          >
            <InfiniteScroll
              dataLength={data.length}
              next={() => fetchMoreParties(false)}
              hasMore={hasMore}
              loader={
                <Box sx={{ textAlign: "center", py: 1 }}>
                  <CircularProgress size={20} sx={{ color: T.primary }} />
                </Box>
              }
              scrollableTarget="freight-list-container"
              style={{ overflow: "visible" }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 2800, // ← updated
                  borderCollapse: "collapse",
                  "& td, & th": {
                    py: 0,
                    px: 0.75,
                    fontSize: "0.75rem",
                    borderBottom: `1px solid #ccc`,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                  },
                  "& th": {
                    fontWeight: 700,
                    color: T.headerText,
                    bgcolor: `${T.headerBg} !important`,
                    py: 0.2,
                    height: 28,
                    fontSize: "0.8rem",
                    letterSpacing: "0.01em",
                    borderBottom: `1px solid #ccc`,
                    lineHeight: 1.2,
                  },
                  "& td": { height: 26 },
                }}
              >
                {/* ── TableHead ── */}
                <TableHead>
                  <TableRow>
                    {[
                      { l: "Settle", sticky: true, left: 0, w: 40 },
                      { l: "#", sticky: true, left: 40, w: 35 },
                      { l: "LR No", sticky: true, left: 75, w: 80 }, // ← now sticky, left: 75
                      { l: "Truck No", sticky: true, left: 155, w: 85 }, // ← now sticky, left: 75+80=155
                      { l: "Party", w: 120 },
                      { l: "Loading Date", w: 85 },
                      { l: "Loading Out", w: 100 },
                      { l: "Unloading Date", w: 100 },
                      { l: "Unloading Out", w: 120 },
                      { l: "From", w: 90 },
                      { l: "To", w: 90 },
                      { l: "E-way Bill No", w: 100 },
                      { l: "E-way Bill Date", w: 100 },
                      { l: "E-way Validity", w: 130 },
                      { l: "Invoice No", w: 100 },
                      { l: "Classification", w: 130 },
                      { l: "Packing Method", w: 130 },
                      { l: "Truck Owner", w: 110 },
                      { l: "Phone", w: 95 },
                      { l: "Ref No", w: 90 },
                      { l: "Remark", w: 140 },
                      { l: "Additional Info", w: 160 },
                      { l: "Date", w: 90 },
                      { l: "Actions", sticky: true, right: 0, w: 50 },
                    ].map(({ l, sticky, left, right, w }) => (
                      <TableCell
                        key={l}
                        sx={{
                          minWidth: w,
                          bgcolor: `${T.headerBg} !important`,
                          color: `${T.headerText} !important`,
                          ...(sticky && {
                            position: "sticky",
                            ...(left !== undefined && { left }),
                            ...(right !== undefined && { right }),
                            zIndex: 12,
                          }),
                        }}
                      >
                        {l}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                {/* ── TableBody ── */}
                <TableBody>
                  {loading && data.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={24}
                        sx={{ textAlign: "center", py: 6, border: "none" }}
                      >
                        <CircularProgress sx={{ color: T.primary }} size={32} />
                      </TableCell>
                    </TableRow>
                  ) : currentData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={24}
                        sx={{ textAlign: "center", py: 6, border: "none" }}
                      >
                        <TruckIcon
                          sx={{
                            fontSize: 40,
                            color: T.border,
                            mb: 1,
                            display: "block",
                            mx: "auto",
                          }}
                        />
                        <Typography
                          sx={{
                            color: T.textSub,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                          }}
                        >
                          No records found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    currentData.map((row, idx) => {
                      const isEven = idx % 2 === 0;
                      const rowBg = isEven ? T.baseRow : T.altRow;
                      const sc = {
                        bgcolor: `${rowBg} !important`,
                        zIndex: 5,
                      };

                      return (
                        <TableRow
                          key={row.freight_id}
                          sx={{
                            "& > td": {
                              bgcolor: rowBg,
                              transition: "background-color 0.12s",
                            },
                            "&:hover > td": {
                              bgcolor: `${T.rowHover} !important`,
                            },
                          }}
                        >
                          {/* ── Settle ── sticky left 0 */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 0,
                              ...sc,
                              p: "2px 6px",
                            }}
                          >
                            <Checkbox
                              size="small"
                              disabled={row.is_completed}
                              onClick={(e) => {
                                setSettleAnchorEl(e.currentTarget);
                                setSettleId(row.freight_id);
                              }}
                              sx={{
                                p: 0,
                                color: T.primary,
                                "&.Mui-checked": { color: T.primary },
                              }}
                            />
                          </TableCell>

                          {/* ── # ── sticky left 40 */}
                          <TableCell
                            sx={{ position: "sticky", left: 40, ...sc }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                color: T.textMuted,
                              }}
                            >
                              {idx + 1}
                            </Typography>
                          </TableCell>

                          {/* ── Truck No ── sticky left 75 */}
                          <TableCell
                            sx={{ position: "sticky", left: 75, ...sc }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: T.text,
                              }}
                            >
                              {row.lr_number || "—"}
                            </Typography>
                          </TableCell>

                          {/* ── Truck No ── sticky left 155 */}
                          <TableCell
                            sx={{ position: "sticky", left: 155, ...sc }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: T.primary,
                                fontSize: "0.75rem",
                              }}
                            >
                              {row.truck_no || "—"}
                            </Typography>
                          </TableCell>

                          {/* ── Party ── */}
                          <TableCell>
                            <Tooltip title={row.party_name || ""} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  maxWidth: 120,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.party_name || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* ── Loading Date ── */}
                          <TableCell>
                            <Typography
                              sx={{ fontSize: "0.72rem", color: T.text }}
                            >
                              {formatDate(row.loading_date)}
                            </Typography>
                          </TableCell>

                          {/* ── Loading Out ── */}
                          <TableCell>
                            <Typography
                              sx={{ fontSize: "0.72rem", color: T.text }}
                            >
                              {formatDate(row.loading_out_date)}
                            </Typography>
                          </TableCell>

                          {/* ── Unloading Date ── */}
                          <TableCell>
                            <Typography
                              sx={{ fontSize: "0.72rem", color: T.text }}
                            >
                              {formatDate(row.unloading_date)}
                            </Typography>
                          </TableCell>

                          {/* ── Unloading Out ── */}
                          <TableCell>
                            <Typography
                              sx={{ fontSize: "0.72rem", color: T.text }}
                            >
                              {formatDate(row.unloading_out_date)}
                            </Typography>
                          </TableCell>

                          {/* ── From ── */}
                          <TableCell>
                            <Tooltip title={row.from_location || ""} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  maxWidth: 90,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.from_location || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* ── To ── */}
                          <TableCell>
                            <Tooltip title={row.to_location || ""} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  maxWidth: 90,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.to_location || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* ── E-way Bill No ── */}
                          <TableCell>
                            <Typography
                              sx={{ fontSize: "0.72rem", color: T.text }}
                            >
                              {row.eway_bill_no || "—"}
                            </Typography>
                          </TableCell>

                          {/* ── E-way Bill Date ── */}
                          <TableCell>
                            <Typography
                              sx={{ fontSize: "0.72rem", color: T.text }}
                            >
                              {formatDate(row.eway_bill_date)}
                            </Typography>
                          </TableCell>

                          {/* ── E-way Validity ── */}
                          <TableCell>
                            <Typography
                              sx={{ fontSize: "0.72rem", color: T.text }}
                            >
                              {formatDate(row.eway_validity_date)}
                            </Typography>
                          </TableCell>

                          {/* ── Invoice No ── */}
                          <TableCell>
                            <Typography
                              sx={{ fontSize: "0.72rem", color: T.text }}
                            >
                              {row.invoice_no || "—"}
                            </Typography>
                          </TableCell>

                          {/* ── Classification ── */}
                          <TableCell>
                            <Tooltip
                              title={row.classification_of_goods || ""}
                              arrow
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  maxWidth: 120,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.classification_of_goods || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* ── Packing Method ── */}
                          <TableCell>
                            <Tooltip title={row.method_of_packing || ""} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  maxWidth: 120,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.method_of_packing || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* ── Truck Owner ── */}
                          <TableCell>
                            <Tooltip title={row.truck_owner || ""} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  maxWidth: 100,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.truck_owner || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* ── Phone ── */}
                          <TableCell>
                            <Typography
                              sx={{ fontSize: "0.72rem", color: T.text }}
                            >
                              {row.phone_no || "—"}
                            </Typography>
                          </TableCell>

                          {/* ── Ref No (tp_no) ── */}
                          <TableCell>
                            <Typography
                              sx={{ fontSize: "0.72rem", color: T.text }}
                            >
                              {row.tp_no || "—"}
                            </Typography>
                          </TableCell>

                          {/* ── Remark ── */}
                          <TableCell>
                            <Tooltip title={row.remark || ""} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  maxWidth: 130,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.remark || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* ── Additional Info ── */}
                          <TableCell>
                            <Tooltip title={row.additional_info || ""} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  maxWidth: 150,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.additional_info || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* ── Date (created_at) ── */}
                          <TableCell>
                            <Chip
                              label={formatDate(row.created_at)}
                              size="small"
                              sx={{
                                fontSize: "0.6rem",
                                height: 16,
                                ...sq,
                                bgcolor: T.primaryBg,
                                color: T.primary,
                                border: `1px solid ${T.border}`,
                                fontWeight: 700,
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                            />
                          </TableCell>

                          {/* ── Actions ── sticky right */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              right: 0,
                              ...sc,
                              py: "2px !important",
                            }}
                          >
                            <ActionButton
                              tip="Print"
                              icon={<PrintIcon />}
                              isPrint
                              onClick={() => handlePrintSingleRow(row)}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </InfiniteScroll>
          </TableContainer>
        </Paper>
      )}

      {/* ── Edit Dialog ── */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...sq,
            border: `1.5px solid ${T.border}`,
            boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
            overflow: "hidden",
          },
        }}
      >
        <DialogHeader
          title="Edit LR Record"
          onClose={() => setEditDialogOpen(false)}
        />
        <DialogContent sx={{ p: 2.5, bgcolor: T.primaryBg }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={editRow.is_remember || false}
                onChange={(e) =>
                  setEditRow((p) => ({ ...p, is_remember: e.target.checked }))
                }
                sx={{ color: T.primary, "&.Mui-checked": { color: T.primary } }}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.75rem" }}>Remember</Typography>
            }
            sx={{ mb: 1.5 }}
          />
          <Grid container spacing={1.5}>
            {[
              { name: "from_location", label: "From" },
              { name: "to_location", label: "To" },
              { name: "loading_date", label: "Loading Date", type: "date" },
              { name: "unloading_date", label: "Unloading Date", type: "date" },
              { name: "truck_no", label: "Truck No" },
              { name: "truck_owner", label: "Truck Owner" },
              { name: "phone_no", label: "Phone No." },
              { name: "pan_no", label: "PAN No" },
              // { name: "tp_no", label: "TP No" },           // removed
              // { name: "do_no", label: "DO No" },            // removed
              // { name: "loading_wt", label: "Loading Wt", type: "number" },
              // { name: "received_wt", label: "Receiving Wt", type: "number" },
              // { name: "freight_rate", label: "LR Rate", type: "number" },
              // { name: "shortage_rate", label: "Shortage Rate", type: "number" },
              // { name: "tds_per", label: "TDS %", type: "number" },
              // { name: "commission", label: "Commission", type: "number" },
              // { name: "ad_mines_exp", label: "Ad Mines", type: "number" },
              // { name: "ad_diesel_exp", label: "Ad Diesel", type: "number" },
              // { name: "ad_cash_exp", label: "Ad Cash", type: "number" },
              // { name: "other_exp", label: "Others Exp.", type: "number" },
            ].map(({ name, label, type }) => (
              <Grid item xs={6} md={3} key={name}>
                <TextField
                  label={label}
                  fullWidth
                  size="small"
                  type={type || "text"}
                  value={editRow[name] || ""}
                  onChange={(e) => handleEditChange(name, e.target.value)}
                  InputLabelProps={type === "date" ? { shrink: true } : {}}
                  sx={inputSx}
                />
              </Grid>
            ))}
            <Grid item xs={6} md={3}>
              <Autocomplete
                options={partyOptions}
                getOptionLabel={(o) => o?.name || ""}
                value={
                  partyOptions.find((o) => o.name === editRow.party_name) ||
                  null
                }
                onChange={(_, v) =>
                  handleEditChange("party_id", v?.party_id || "")
                }
                renderInput={(p) => (
                  <TextField
                    {...p}
                    label="Party Name"
                    size="small"
                    sx={inputSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Remark"
                fullWidth
                multiline
                rows={2}
                size="small"
                value={editRow.remark || ""}
                onChange={(e) => handleEditChange("remark", e.target.value)}
                sx={inputSx}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            gap: 1,
            bgcolor: T.primaryBg,
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <Button
            size="small"
            onClick={() => setEditDialogOpen(false)}
            sx={{ ...ghostBtn, height: 32, px: 2 }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            onClick={handleEditSave}
            sx={{ ...primaryBtn, height: 32, px: 2.5 }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Export Dialog ── */}
      <Dialog
        open={excelModalOpen}
        onClose={() => setExcelModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            ...sq,
            border: `1.5px solid ${T.border}`,
            boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
            overflow: "hidden",
          },
        }}
      >
        <DialogHeader
          title="Select Rows for Excel Export"
          onClose={() => setExcelModalOpen(false)}
        />
        <DialogContent sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by party name…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon sx={{ fontSize: 14, color: T.primary }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1.5, ...filterInputSx }}
          />
          <TableContainer sx={{ maxHeight: "50vh", overflow: "auto" }}>
            <Table
              stickyHeader
              size="small"
              sx={{
                "& td, & th": {
                  fontSize: "0.75rem",
                  borderBottom: `1px solid #ccc`,
                  lineHeight: 1.2,
                  height: 26,
                },
                "& th": {
                  fontWeight: 700,
                  color: T.headerText,
                  bgcolor: `${T.headerBg} !important`,
                  fontSize: "0.8rem",
                  letterSpacing: "0.01em",
                  borderBottom: `1px solid #ccc`,
                  height: 28,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  {[
                    "",
                    "LR ID",
                    "Truck No",
                    "Party Name",
                    "Loading Date",
                    "Net Payable",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        bgcolor: `${T.headerBg} !important`,
                        color: `${T.headerText} !important`,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {currentData
                  .filter((r) =>
                    r.party_name
                      ?.toLowerCase()
                      .includes(searchQuery.toLowerCase()),
                  )
                  .map((row, idx) => {
                    const isEven = idx % 2 === 0;
                    const rowBg = isEven ? T.baseRow : T.altRow;
                    return (
                      <TableRow
                        key={row.freight_id}
                        sx={{
                          "& > td": { bgcolor: rowBg },
                          "&:hover > td": {
                            bgcolor: `${T.rowHover} !important`,
                          },
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={selectedRows.includes(row.freight_id)}
                            onChange={(e) =>
                              setSelectedRows((p) =>
                                e.target.checked
                                  ? [...p, row.freight_id]
                                  : p.filter((id) => id !== row.freight_id),
                              )
                            }
                            sx={{
                              color: T.primary,
                              "&.Mui-checked": { color: T.primary },
                            }}
                          />
                        </TableCell>
                        <TableCell>{row.freight_id}</TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: T.primary,
                              fontSize: "0.75rem",
                            }}
                          >
                            {row.truck_no}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.party_name}</TableCell>
                        <TableCell>{formatDate(row.loading_date)}</TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: T.primary,
                              fontSize: "0.75rem",
                            }}
                          >
                            ₹{row.net_payable}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            gap: 1,
            bgcolor: T.primaryBg,
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <Button
            size="small"
            onClick={() => setExcelModalOpen(false)}
            sx={{ ...ghostBtn, height: 32, px: 2 }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            onClick={handleExportSelectedToExcel}
            sx={{ ...primaryBtn, height: 32, px: 2.5 }}
          >
            Export Selected ({selectedRows.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Popovers ── */}
      <ConfirmPopover
        anchorEl={settleAnchorEl}
        onClose={() => {
          setSettleAnchorEl(null);
          setSettleId(null);
        }}
        onConfirm={() => handleComplete(settleId)}
        title="Settle this LR?"
        message="Mark this record as completed?"
        isSettle
      />
      <ConfirmPopover
        anchorEl={deleteAnchorEl}
        onClose={() => {
          setDeleteAnchorEl(null);
          setDeleteId(null);
        }}
        onConfirm={() => handleDelete(deleteId)}
        title="Delete LR Record?"
        message="This action cannot be undone."
      />

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          sx={{ ...sq, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
